import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import SiteSurveyResponseTable from '../../../components/tables/SiteSurveyResponseTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useSiteSurveyResponseSearch, useDeleteSiteSurveyResponse } from '../../../hooks/useSiteSurveyResponseApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { SiteSurveyResponseDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const SiteSurveyResponseList: React.FC = () => {
  const [formFilters, setFormFilters] = useState<any>({}); // Form input values
  const [searchFilters, setSearchFilters] = useState<any>({}); // Actual search criteria
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [items, setItems] = useState<SiteSurveyResponseDTO[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { search } = useSiteSurveyResponseSearch();
  const deleteSiteSurveyResponse = useDeleteSiteSurveyResponse();

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Site Survey Responses', href: '#' },
  ];

  const totalPages = Math.ceil(totalRecords / pageSize);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const response = await search({
        page: currentPage,
        pageSize,
        sortBy: 'CreatedUtc',
        sortDescending: true,
        filters: searchFilters,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching Site Survey Responses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [currentPage, pageSize, searchFilters]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchFilters(formFilters);
  };

  const handleClearSearch = () => {
    setFormFilters({});
    setSearchFilters({});
    setCurrentPage(1);
  };

  const handleDelete = async (dto: SiteSurveyResponseDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteSiteSurveyResponse.mutateAsync(dto.id || '');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting SiteSurveyResponse:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/sitesurveyresponses"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Settings.SiteSurveyResponse.View'
    >
      <Card
        title="Manage Site Survey Responses"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/sitesurveyresponses/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <SiteSurveyResponseTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/sitesurveyresponses/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default SiteSurveyResponseList;
